'use client';

import React from 'react';
import { cn } from '@/lib/utils';
import { Message } from '@/type/chat';
import AlfaGptMenuIcon from '../Icons/AlfaGptMenuIcon';
import StepIcon from '../Icons/StepIcon';
import { useTranslation } from '@/lib/i18n';
import { Avatar, AvatarGroup } from '@mui/material';
import { Globe } from 'lucide-react';

function getSourceUrl(source: any) {
  return source?.metadata?.url ?? source?.source_url ?? source?.url;
}

interface MessageBoxTabProps {
  message: Message;
  showSteps?: boolean;
  activeTab: 'pythra' | 'steps' | 'sources';
  onTabChange: (tab: 'pythra' | 'steps' | 'sources') => void;
}

const MessageBoxTab: React.FC<MessageBoxTabProps> = ({
  message,
  showSteps,
  activeTab,
  onTabChange,
}) => {
  const { t } = useTranslation();
  const sources = message.sources ?? message.webSources ?? [];

  // ✅ 检查是否是被取消的消息（没有内容）
  const isCancelled = message.cancelled && !message.content;

  // 提取域名的辅助函数
  const extractDomain = (url: string) => {
    try {
      const urlObj = new URL(url);
      return urlObj.hostname;
    } catch {
      return url;
    }
  };

  // 按域名去重，确保显示不同来源的favicon
  const uniqueDomainSources = sources?.reduce((acc: any[], item: any) => {
    const url = getSourceUrl(item);
    if (!url) return acc;

    const domain = extractDomain(url);
    const isDuplicate = acc.some((existingItem) => {
      const existingUrl = getSourceUrl(existingItem);
      return extractDomain(existingUrl) === domain;
    });

    if (!isDuplicate) {
      acc.push(item);
    }
    return acc;
  }, []);

  const sourceUrl = uniqueDomainSources?.map(getSourceUrl).filter(Boolean) || [];

  return (
    <div className="flex flex-col gap-3">
      <div className="flex gap-4 items-start relative">
        {/* Pythra Tab */}
        <div className="relative">
          <button
            onClick={() => onTabChange('pythra')}
            className={cn(
              'flex gap-2 h-9 items-center px-2 py-1 rounded-[8px] transition-all',
              activeTab === 'pythra'
                ? ' text-text-primary'
                : ' text-text-secondary hover:bg-gray-50 dark:hover:bg-[#171717]',
            )}
          >
            <AlfaGptMenuIcon size={16} />
            <span className="text-[16px]">{t('Pythra')}</span>
          </button>
          {/* Indicator line for Pythra tab */}
          {/* ✅ 被取消的消息不显示指示器（因为只有一个 tab） */}
          {activeTab === 'pythra' && (
            <div className="absolute bottom-[-12px] left-0 right-0 h-[3px] bg-black dark:bg-white" />
          )}
        </div>

        {/* Steps Tab */}
        {/* ✅ 被取消的消息不显示 Steps tab */}
        {!isCancelled && showSteps && message.thinkStatus && message.thinkStatus.length > 0 && (
          <div className="relative">
            <button
              onClick={() => onTabChange('steps')}
              className={cn(
                'flex gap-2 h-9 items-center px-2 py-1 rounded-lg transition-all',
                activeTab === 'steps'
                  ? ' text-text-primary'
                  : ' text-text-secondary hover:bg-gray-50 dark:hover:bg-[#171717]',
              )}
            >
              <StepIcon size={16} />
              <span className="text-[16px]">{t('Steps')}</span>
            </button>
            {/* Indicator line for Steps tab */}
            {activeTab === 'steps' && (
              <div className="absolute -bottom-3 left-0 right-0 h-0.75 bg-black dark:bg-white" />
            )}
          </div>
        )}

        {/* Sources Tab */}
        {/* ✅ 被取消的消息不显示 Sources tab */}
        {!isCancelled && sources && sources.length > 0 && (
          <div className="relative">
            <button
              onClick={() => onTabChange('sources')}
              className={cn(
                'flex gap-2 h-9 items-center px-2 py-1 rounded-lg transition-all',
                activeTab === 'sources'
                  ? ' text-text-primary'
                  : ' text-text-secondary hover:bg-gray-50 dark:hover:bg-[#171717]',
              )}
            >
              <div className="flex items-center gap-1">
                <AvatarGroup max={3}>
                  {sourceUrl.slice(0, 3).map((item, i) => (
                    <Avatar
                      key={i}
                      alt="favicon"
                      sx={{ width: 20, height: 20 }}
                      classes={{
                        root: 'border-2! border-[#1E1E1E]! bg-gray-100! dark:bg-[#191B1D]!',
                      }}
                      src={`https://s2.googleusercontent.com/s2/favicons?domain_url=${item}`}
                    >
                      <Globe size={14} className="text-text-tertiary" />
                    </Avatar>
                  ))}
                </AvatarGroup>
                <span className="text-[16px] ml-1">
                  {sources.length} {t('Sources')}
                </span>
              </div>
            </button>
            {/* Indicator line for Sources tab */}
            {activeTab === 'sources' && (
              <div className="absolute -bottom-3 left-0 right-0 h-0.75 bg-black dark:bg-white" />
            )}
          </div>
        )}
      </div>

      {/* Horizontal divider line */}
      <div className="h-0 border-t border-border-divider w-full" />
    </div>
  );
};

export default MessageBoxTab;
